// CMS base costs (annual, in USD) - Updated with 2024/2025 pricing research
// All costs are now tiered by organization size for accuracy
// Tiers: startup, midmarket, enterprise, largeEnterprise

export const CMS_DATA = {
  // ============================================
  // WEBSITE BUILDERS (Entry-level, all-in-one)
  // ============================================
  squarespace: {
    name: 'Squarespace',
    category: 'Website Builder',
    // Business plan pricing - relatively flat across sizes
    licensing: {
      startup: 276,         // Business plan $23/mo
      midmarket: 324,       // Business plan + extras
      enterprise: 468,      // Commerce Advanced
      largeEnterprise: 588, // Commerce Advanced + add-ons
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,           // Included
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 3,
      midmarket: 5,
      enterprise: 8,
      largeEnterprise: 12,
    },
    devHoursPerStaticPage: 0.5,
    devHoursPerDynamicPage: 2,
    avgBuildWeeks: 4,
    seoAuditHours: 16,
    redesignMultiplier: 1.1,
    companyTier: 'startup',
  },
  wix: {
    name: 'Wix',
    category: 'Website Builder',
    licensing: {
      startup: 204,         // Core plan $17/mo
      midmarket: 348,       // Business plan $29/mo
      enterprise: 432,      // Business Elite $36/mo
      largeEnterprise: 600, // Enterprise solutions
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 3,
      midmarket: 5,
      enterprise: 8,
      largeEnterprise: 12,
    },
    devHoursPerStaticPage: 0.5,
    devHoursPerDynamicPage: 2,
    avgBuildWeeks: 4,
    seoAuditHours: 16,
    redesignMultiplier: 1.1,
    companyTier: 'startup',
  },
  weebly: {
    name: 'Weebly',
    category: 'Website Builder',
    licensing: {
      startup: 144,         // Professional $12/mo
      midmarket: 180,       // Performance $15/mo (legacy)
      enterprise: 300,      // Business features
      largeEnterprise: 420, // Advanced needs
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 3,
      midmarket: 5,
      enterprise: 8,
      largeEnterprise: 10,
    },
    devHoursPerStaticPage: 0.5,
    devHoursPerDynamicPage: 2,
    avgBuildWeeks: 4,
    seoAuditHours: 16,
    redesignMultiplier: 1.1,
    companyTier: 'startup',
  },
  duda: {
    name: 'Duda',
    category: 'Website Builder',
    licensing: {
      startup: 228,         // Basic $19/mo
      midmarket: 444,       // Team $37/mo
      enterprise: 708,      // Agency $59/mo
      largeEnterprise: 1188, // White Label $99/mo
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 4,
      midmarket: 6,
      enterprise: 10,
      largeEnterprise: 15,
    },
    devHoursPerStaticPage: 0.5,
    devHoursPerDynamicPage: 2.5,
    avgBuildWeeks: 5,
    seoAuditHours: 18,
    redesignMultiplier: 1.1,
    companyTier: 'startup',
  },

  // ============================================
  // E-COMMERCE PLATFORMS
  // ============================================
  shopify: {
    name: 'Shopify',
    category: 'E-commerce Platform',
    licensing: {
      startup: 468,         // Basic $39/mo
      midmarket: 1260,      // Shopify $105/mo
      enterprise: 4788,     // Advanced $399/mo
      largeEnterprise: 28800, // Shopify Plus $2,400/mo
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,           // Included
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 8,
      midmarket: 15,
      enterprise: 25,
      largeEnterprise: 40,
    },
    devHoursPerStaticPage: 1,
    devHoursPerDynamicPage: 4,
    avgBuildWeeks: 8,
    seoAuditHours: 24,
    redesignMultiplier: 1.2,
    companyTier: 'startup',
  },
  shopifyPlus: {
    name: 'Shopify Plus',
    category: 'Enterprise E-commerce',
    licensing: {
      startup: 28800,       // Plus starts at $2,400/mo (rare for startups)
      midmarket: 28800,     // $2,400/mo
      enterprise: 36000,    // $3,000/mo with volume
      largeEnterprise: 60000, // $5,000/mo enterprise
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 20,
      midmarket: 25,
      enterprise: 35,
      largeEnterprise: 50,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 6,
    avgBuildWeeks: 12,
    seoAuditHours: 35,
    redesignMultiplier: 1.25,
    companyTier: 'enterprise',
  },
  bigcommerce: {
    name: 'BigCommerce',
    category: 'E-commerce Platform',
    licensing: {
      startup: 468,         // Standard $39/mo
      midmarket: 1068,      // Plus $89/mo (or Pro $299 at higher GMV)
      enterprise: 3588,     // Pro $299/mo
      largeEnterprise: 12000, // Enterprise custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 10,
      midmarket: 18,
      enterprise: 28,
      largeEnterprise: 40,
    },
    devHoursPerStaticPage: 1,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 28,
    redesignMultiplier: 1.2,
    companyTier: 'midmarket',
  },
  woocommerce: {
    name: 'WooCommerce',
    category: 'E-commerce Plugin',
    // Free plugin but requires hosting + extensions
    licensing: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    perSiteCost: {
      startup: 500,         // Basic plugins/themes
      midmarket: 1500,      // Premium plugins
      enterprise: 3000,     // Enterprise extensions
      largeEnterprise: 6000, // Full suite
    },
    hostingPerSite: {
      startup: 600,         // Shared hosting
      midmarket: 1800,      // Managed WP hosting
      enterprise: 4800,     // Premium managed
      largeEnterprise: 12000, // Enterprise hosting
    },
    maintenanceHoursPerMonth: {
      startup: 15,
      midmarket: 25,
      enterprise: 40,
      largeEnterprise: 60,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 6,
    avgBuildWeeks: 12,
    seoAuditHours: 30,
    redesignMultiplier: 1.25,
    companyTier: 'startup',
  },
  magento: {
    name: 'Adobe Commerce (Magento)',
    category: 'Enterprise E-commerce',
    licensing: {
      startup: 22000,       // Magento Open Source + support (rare)
      midmarket: 40000,     // Adobe Commerce
      enterprise: 80000,    // Adobe Commerce Pro
      largeEnterprise: 150000, // Adobe Commerce Enterprise
    },
    perSiteCost: {
      startup: 5000,
      midmarket: 15000,
      enterprise: 25000,
      largeEnterprise: 50000,
    },
    hostingPerSite: {
      startup: 12000,
      midmarket: 24000,
      enterprise: 48000,
      largeEnterprise: 96000,
    },
    maintenanceHoursPerMonth: {
      startup: 40,
      midmarket: 60,
      enterprise: 80,
      largeEnterprise: 120,
    },
    devHoursPerStaticPage: 3,
    devHoursPerDynamicPage: 12,
    avgBuildWeeks: 24,
    seoAuditHours: 50,
    redesignMultiplier: 1.4,
    companyTier: 'enterprise',
  },

  // ============================================
  // TRADITIONAL CMS (Self-hosted / Open Source)
  // ============================================
  wordpressSelfHosted: {
    name: 'WordPress (Self-hosted)',
    category: 'Open Source CMS',
    licensing: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    perSiteCost: {
      startup: 200,         // Free/cheap themes & plugins
      midmarket: 500,       // Premium themes/plugins
      enterprise: 1500,     // Enterprise plugins
      largeEnterprise: 3000, // Full premium suite
    },
    hostingPerSite: {
      startup: 300,         // Shared hosting
      midmarket: 1200,      // Managed WP hosting
      enterprise: 3600,     // Premium managed
      largeEnterprise: 9600, // Enterprise hosting
    },
    maintenanceHoursPerMonth: {
      startup: 8,
      midmarket: 15,
      enterprise: 25,
      largeEnterprise: 40,
    },
    devHoursPerStaticPage: 1,
    devHoursPerDynamicPage: 4,
    avgBuildWeeks: 8,
    seoAuditHours: 25,
    redesignMultiplier: 1.2,
    companyTier: 'startup',
  },
  wordpress: {
    name: 'WordPress VIP',
    category: 'Enterprise CMS',
    licensing: {
      startup: 15000,       // Entry tier (rare for startups)
      midmarket: 25000,     // Standard
      enterprise: 50000,    // Professional
      largeEnterprise: 100000, // Enterprise
    },
    perSiteCost: {
      startup: 2000,
      midmarket: 5000,
      enterprise: 10000,
      largeEnterprise: 20000,
    },
    hostingPerSite: {
      startup: 0,           // Included in VIP
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 20,
      midmarket: 35,
      enterprise: 50,
      largeEnterprise: 70,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 6,
    avgBuildWeeks: 14,
    seoAuditHours: 35,
    redesignMultiplier: 1.25,
    companyTier: 'midmarket',
  },
  drupal: {
    name: 'Drupal',
    category: 'Open Source CMS',
    licensing: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    perSiteCost: {
      startup: 2000,        // Basic modules/themes
      midmarket: 8000,      // Custom modules
      enterprise: 15000,    // Enterprise customization
      largeEnterprise: 30000, // Full enterprise build
    },
    hostingPerSite: {
      startup: 3600,        // Basic managed Drupal
      midmarket: 12000,     // Professional hosting
      enterprise: 24000,    // Enterprise hosting
      largeEnterprise: 48000, // Premium enterprise
    },
    maintenanceHoursPerMonth: {
      startup: 25,
      midmarket: 45,
      enterprise: 65,
      largeEnterprise: 90,
    },
    devHoursPerStaticPage: 2.5,
    devHoursPerDynamicPage: 10,
    avgBuildWeeks: 18,
    seoAuditHours: 45,
    redesignMultiplier: 1.35,
    companyTier: 'midmarket',
  },
  joomla: {
    name: 'Joomla',
    category: 'Open Source CMS',
    licensing: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    perSiteCost: {
      startup: 150,
      midmarket: 300,
      enterprise: 600,
      largeEnterprise: 1200,
    },
    hostingPerSite: {
      startup: 360,
      midmarket: 1200,
      enterprise: 3000,
      largeEnterprise: 6000,
    },
    maintenanceHoursPerMonth: {
      startup: 12,
      midmarket: 20,
      enterprise: 30,
      largeEnterprise: 45,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 28,
    redesignMultiplier: 1.2,
    companyTier: 'startup',
  },
  typo3: {
    name: 'TYPO3',
    category: 'Open Source CMS',
    licensing: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    perSiteCost: {
      startup: 2000,
      midmarket: 5000,
      enterprise: 10000,
      largeEnterprise: 20000,
    },
    hostingPerSite: {
      startup: 3600,
      midmarket: 9600,
      enterprise: 18000,
      largeEnterprise: 36000,
    },
    maintenanceHoursPerMonth: {
      startup: 25,
      midmarket: 40,
      enterprise: 55,
      largeEnterprise: 75,
    },
    devHoursPerStaticPage: 2,
    devHoursPerDynamicPage: 8,
    avgBuildWeeks: 16,
    seoAuditHours: 40,
    redesignMultiplier: 1.3,
    companyTier: 'midmarket',
  },
  craftcms: {
    name: 'Craft CMS',
    category: 'Premium CMS',
    licensing: {
      startup: 0,           // Solo (free)
      midmarket: 299,       // Pro license (one-time, annual ~$59 renewal)
      enterprise: 599,      // Pro + plugins
      largeEnterprise: 999, // Enterprise license
    },
    perSiteCost: {
      startup: 300,
      midmarket: 1000,
      enterprise: 2000,
      largeEnterprise: 4000,
    },
    hostingPerSite: {
      startup: 600,
      midmarket: 1800,
      enterprise: 4200,
      largeEnterprise: 9600,
    },
    maintenanceHoursPerMonth: {
      startup: 10,
      midmarket: 18,
      enterprise: 28,
      largeEnterprise: 40,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 28,
    redesignMultiplier: 1.2,
    companyTier: 'startup',
  },
  ghost: {
    name: 'Ghost',
    category: 'Publishing Platform',
    licensing: {
      startup: 108,         // Starter $9/mo
      midmarket: 300,       // Creator $25/mo
      enterprise: 600,      // Team $50/mo
      largeEnterprise: 1788, // Business $149/mo
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    hostingPerSite: {
      startup: 0,           // Included with Ghost(Pro)
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 4,
      midmarket: 8,
      enterprise: 12,
      largeEnterprise: 20,
    },
    devHoursPerStaticPage: 0.75,
    devHoursPerDynamicPage: 3,
    avgBuildWeeks: 6,
    seoAuditHours: 20,
    redesignMultiplier: 1.15,
    companyTier: 'startup',
  },
  umbraco: {
    name: 'Umbraco',
    category: 'Open Source CMS',
    licensing: {
      startup: 0,           // Community (free)
      midmarket: 0,
      enterprise: 4800,     // Umbraco Cloud Pro €400/mo
      largeEnterprise: 12000, // Enterprise Cloud
    },
    perSiteCost: {
      startup: 1000,
      midmarket: 2000,
      enterprise: 4000,
      largeEnterprise: 8000,
    },
    hostingPerSite: {
      startup: 1800,
      midmarket: 4800,
      enterprise: 0,        // Included in Cloud
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 18,
      midmarket: 30,
      enterprise: 45,
      largeEnterprise: 65,
    },
    devHoursPerStaticPage: 2,
    devHoursPerDynamicPage: 7,
    avgBuildWeeks: 14,
    seoAuditHours: 35,
    redesignMultiplier: 1.25,
    companyTier: 'midmarket',
  },

  // ============================================
  // HEADLESS CMS
  // ============================================
  contentful: {
    name: 'Contentful',
    category: 'Headless CMS',
    licensing: {
      startup: 0,           // Free community tier
      midmarket: 3600,      // Team $300/mo
      enterprise: 36000,    // Enterprise starts ~$3k/mo
      largeEnterprise: 72000, // Enterprise+ custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 1200,      // Additional environments
      enterprise: 5000,
      largeEnterprise: 15000,
    },
    hostingPerSite: {
      startup: 1200,        // Vercel/Netlify basic
      midmarket: 3600,      // Vercel Pro
      enterprise: 12000,    // Enterprise hosting
      largeEnterprise: 24000,
    },
    maintenanceHoursPerMonth: {
      startup: 20,
      midmarket: 35,
      enterprise: 55,
      largeEnterprise: 80,
    },
    devHoursPerStaticPage: 2,
    devHoursPerDynamicPage: 8,
    avgBuildWeeks: 16,
    seoAuditHours: 40,
    redesignMultiplier: 1.3,
    companyTier: 'enterprise',
  },
  strapi: {
    name: 'Strapi',
    category: 'Headless CMS',
    licensing: {
      startup: 0,           // Self-hosted (free)
      midmarket: 1188,      // Bronze $99/mo per seat
      enterprise: 5988,     // Silver $499/mo
      largeEnterprise: 18000, // Gold custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 600,
      enterprise: 2000,
      largeEnterprise: 5000,
    },
    hostingPerSite: {
      startup: 600,         // Basic VPS
      midmarket: 2400,      // Managed hosting
      enterprise: 6000,     // Enterprise hosting
      largeEnterprise: 15000,
    },
    maintenanceHoursPerMonth: {
      startup: 18,
      midmarket: 30,
      enterprise: 45,
      largeEnterprise: 65,
    },
    devHoursPerStaticPage: 2,
    devHoursPerDynamicPage: 7,
    avgBuildWeeks: 14,
    seoAuditHours: 40,
    redesignMultiplier: 1.25,
    companyTier: 'startup',
  },
  sanity: {
    name: 'Sanity',
    category: 'Headless CMS',
    // Sanity pricing: Free → $15-20/user → $99/user → Enterprise
    licensing: {
      startup: 480,         // Team ~$20/user × 2 users × 12 months
      midmarket: 2376,      // Growth ~$99/user × 2 users × 12 months
      enterprise: 9576,     // Growth with more users/features
      largeEnterprise: 24000, // Enterprise custom pricing
    },
    perSiteCost: {
      startup: 0,
      midmarket: 500,
      enterprise: 2000,
      largeEnterprise: 5000,
    },
    hostingPerSite: {
      startup: 600,         // Vercel Hobby/Pro
      midmarket: 1800,      // Vercel Pro
      enterprise: 4800,     // Enterprise hosting
      largeEnterprise: 12000,
    },
    maintenanceHoursPerMonth: {
      startup: 12,
      midmarket: 22,
      enterprise: 35,
      largeEnterprise: 50,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 6,
    avgBuildWeeks: 12,
    seoAuditHours: 35,
    redesignMultiplier: 1.2,
    companyTier: 'midmarket',
  },
  prismic: {
    name: 'Prismic',
    category: 'Headless CMS',
    licensing: {
      startup: 84,          // Starter $7/mo
      midmarket: 1200,      // Small $100/mo
      enterprise: 6000,     // Medium $500/mo
      largeEnterprise: 18000, // Large custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 300,
      enterprise: 1000,
      largeEnterprise: 3000,
    },
    hostingPerSite: {
      startup: 600,
      midmarket: 1800,
      enterprise: 4200,
      largeEnterprise: 9600,
    },
    maintenanceHoursPerMonth: {
      startup: 10,
      midmarket: 18,
      enterprise: 28,
      largeEnterprise: 42,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 30,
    redesignMultiplier: 1.2,
    companyTier: 'midmarket',
  },
  hygraph: {
    name: 'Hygraph (GraphCMS)',
    category: 'Headless CMS',
    licensing: {
      startup: 0,           // Community (free)
      midmarket: 2388,      // Professional $199/mo
      enterprise: 5388,     // Team $449/mo
      largeEnterprise: 15000, // Enterprise custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 500,
      enterprise: 1500,
      largeEnterprise: 4000,
    },
    hostingPerSite: {
      startup: 600,
      midmarket: 2400,
      enterprise: 5400,
      largeEnterprise: 12000,
    },
    maintenanceHoursPerMonth: {
      startup: 15,
      midmarket: 25,
      enterprise: 38,
      largeEnterprise: 55,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 6,
    avgBuildWeeks: 12,
    seoAuditHours: 32,
    redesignMultiplier: 1.22,
    companyTier: 'midmarket',
  },
  storyblok: {
    name: 'Storyblok',
    category: 'Headless CMS',
    licensing: {
      startup: 0,           // Community (free)
      midmarket: 1068,      // Entry €89/mo
      enterprise: 5388,     // Teams €449/mo
      largeEnterprise: 18000, // Enterprise custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 400,
      enterprise: 1200,
      largeEnterprise: 3500,
    },
    hostingPerSite: {
      startup: 600,
      midmarket: 2100,
      enterprise: 4800,
      largeEnterprise: 10800,
    },
    maintenanceHoursPerMonth: {
      startup: 12,
      midmarket: 20,
      enterprise: 32,
      largeEnterprise: 48,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5.5,
    avgBuildWeeks: 11,
    seoAuditHours: 30,
    redesignMultiplier: 1.2,
    companyTier: 'midmarket',
  },
  buttercms: {
    name: 'ButterCMS',
    category: 'Headless CMS',
    licensing: {
      startup: 996,         // Micro $83/mo
      midmarket: 2988,      // Startup $249/mo
      enterprise: 5988,     // Small Business $499/mo
      largeEnterprise: 14388, // Enterprise $1199/mo
    },
    perSiteCost: {
      startup: 0,
      midmarket: 300,
      enterprise: 800,
      largeEnterprise: 2000,
    },
    hostingPerSite: {
      startup: 600,
      midmarket: 1800,
      enterprise: 3600,
      largeEnterprise: 7200,
    },
    maintenanceHoursPerMonth: {
      startup: 10,
      midmarket: 16,
      enterprise: 24,
      largeEnterprise: 36,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 28,
    redesignMultiplier: 1.18,
    companyTier: 'startup',
  },
  datocms: {
    name: 'DatoCMS',
    category: 'Headless CMS',
    licensing: {
      startup: 0,           // Free tier
      midmarket: 1188,      // Professional €99/mo
      enterprise: 3588,     // Business €299/mo
      largeEnterprise: 12000, // Enterprise custom
    },
    perSiteCost: {
      startup: 0,
      midmarket: 300,
      enterprise: 1000,
      largeEnterprise: 3000,
    },
    hostingPerSite: {
      startup: 600,
      midmarket: 1800,
      enterprise: 4200,
      largeEnterprise: 9000,
    },
    maintenanceHoursPerMonth: {
      startup: 10,
      midmarket: 18,
      enterprise: 28,
      largeEnterprise: 42,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 28,
    redesignMultiplier: 1.2,
    companyTier: 'midmarket',
  },

  // ============================================
  // MARKETING / HUBSPOT
  // ============================================
  hubspot: {
    name: 'HubSpot CMS Hub',
    category: 'Marketing CMS',
    licensing: {
      startup: 540,         // Starter $45/mo
      midmarket: 4320,      // Professional $360/mo
      enterprise: 14400,    // Enterprise $1,200/mo
      largeEnterprise: 24000, // Enterprise+ with add-ons
    },
    perSiteCost: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 5000,
    },
    hostingPerSite: {
      startup: 0,           // Included
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 8,
      midmarket: 18,
      enterprise: 28,
      largeEnterprise: 42,
    },
    devHoursPerStaticPage: 1.5,
    devHoursPerDynamicPage: 5,
    avgBuildWeeks: 10,
    seoAuditHours: 28,
    redesignMultiplier: 1.2,
    companyTier: 'midmarket',
  },
  hubspotEnterprise: {
    name: 'HubSpot CMS Enterprise',
    category: 'Enterprise Marketing CMS',
    licensing: {
      startup: 14400,       // Enterprise $1,200/mo (rare for startups)
      midmarket: 14400,
      enterprise: 36000,    // Enterprise + Marketing Hub
      largeEnterprise: 72000, // Full suite
    },
    perSiteCost: {
      startup: 0,
      midmarket: 2000,
      enterprise: 5000,
      largeEnterprise: 10000,
    },
    hostingPerSite: {
      startup: 0,
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 18,
      midmarket: 28,
      enterprise: 40,
      largeEnterprise: 60,
    },
    devHoursPerStaticPage: 2,
    devHoursPerDynamicPage: 7,
    avgBuildWeeks: 14,
    seoAuditHours: 38,
    redesignMultiplier: 1.28,
    companyTier: 'enterprise',
  },

  // ============================================
  // ENTERPRISE DXP (Digital Experience Platforms)
  // ============================================
  sitecore: {
    name: 'Sitecore',
    category: 'Enterprise DXP',
    licensing: {
      startup: 40000,       // Rare for startups
      midmarket: 72000,
      enterprise: 120000,
      largeEnterprise: 250000,
    },
    perSiteCost: {
      startup: 10000,
      midmarket: 25000,
      enterprise: 50000,
      largeEnterprise: 100000,
    },
    hostingPerSite: {
      startup: 12000,
      midmarket: 24000,
      enterprise: 48000,
      largeEnterprise: 96000,
    },
    maintenanceHoursPerMonth: {
      startup: 40,
      midmarket: 60,
      enterprise: 90,
      largeEnterprise: 130,
    },
    devHoursPerStaticPage: 3,
    devHoursPerDynamicPage: 12,
    avgBuildWeeks: 24,
    seoAuditHours: 60,
    redesignMultiplier: 1.4,
    companyTier: 'largeEnterprise',
  },
  aem: {
    name: 'Adobe Experience Manager',
    category: 'Enterprise DXP',
    licensing: {
      startup: 100000,      // Very rare for startups
      midmarket: 150000,
      enterprise: 300000,
      largeEnterprise: 500000,
    },
    perSiteCost: {
      startup: 20000,
      midmarket: 40000,
      enterprise: 80000,
      largeEnterprise: 150000,
    },
    hostingPerSite: {
      startup: 24000,
      midmarket: 36000,
      enterprise: 72000,
      largeEnterprise: 144000,
    },
    maintenanceHoursPerMonth: {
      startup: 60,
      midmarket: 80,
      enterprise: 120,
      largeEnterprise: 180,
    },
    devHoursPerStaticPage: 4,
    devHoursPerDynamicPage: 14,
    avgBuildWeeks: 28,
    seoAuditHours: 60,
    redesignMultiplier: 1.5,
    companyTier: 'largeEnterprise',
  },
  kentico: {
    name: 'Kentico',
    category: 'Enterprise DXP',
    licensing: {
      startup: 8000,
      midmarket: 15000,
      enterprise: 30000,
      largeEnterprise: 60000,
    },
    perSiteCost: {
      startup: 3000,
      midmarket: 8000,
      enterprise: 15000,
      largeEnterprise: 30000,
    },
    hostingPerSite: {
      startup: 6000,
      midmarket: 15000,
      enterprise: 30000,
      largeEnterprise: 60000,
    },
    maintenanceHoursPerMonth: {
      startup: 30,
      midmarket: 45,
      enterprise: 65,
      largeEnterprise: 90,
    },
    devHoursPerStaticPage: 2.5,
    devHoursPerDynamicPage: 9,
    avgBuildWeeks: 18,
    seoAuditHours: 45,
    redesignMultiplier: 1.35,
    companyTier: 'enterprise',
  },
  acquia: {
    name: 'Acquia (Drupal Cloud)',
    category: 'Enterprise DXP',
    licensing: {
      startup: 20000,
      midmarket: 50000,
      enterprise: 100000,
      largeEnterprise: 200000,
    },
    perSiteCost: {
      startup: 5000,
      midmarket: 15000,
      enterprise: 30000,
      largeEnterprise: 60000,
    },
    hostingPerSite: {
      startup: 0,           // Included
      midmarket: 0,
      enterprise: 0,
      largeEnterprise: 0,
    },
    maintenanceHoursPerMonth: {
      startup: 35,
      midmarket: 50,
      enterprise: 75,
      largeEnterprise: 110,
    },
    devHoursPerStaticPage: 2.5,
    devHoursPerDynamicPage: 10,
    avgBuildWeeks: 20,
    seoAuditHours: 50,
    redesignMultiplier: 1.35,
    companyTier: 'enterprise',
  },
  optimizely: {
    name: 'Optimizely CMS',
    category: 'Enterprise DXP',
    licensing: {
      startup: 30000,
      midmarket: 60000,
      enterprise: 120000,
      largeEnterprise: 240000,
    },
    perSiteCost: {
      startup: 8000,
      midmarket: 20000,
      enterprise: 40000,
      largeEnterprise: 80000,
    },
    hostingPerSite: {
      startup: 9000,
      midmarket: 18000,
      enterprise: 36000,
      largeEnterprise: 72000,
    },
    maintenanceHoursPerMonth: {
      startup: 35,
      midmarket: 55,
      enterprise: 80,
      largeEnterprise: 115,
    },
    devHoursPerStaticPage: 3,
    devHoursPerDynamicPage: 11,
    avgBuildWeeks: 22,
    seoAuditHours: 55,
    redesignMultiplier: 1.38,
    companyTier: 'enterprise',
  },
  bloomreach: {
    name: 'Bloomreach',
    category: 'Enterprise DXP',
    licensing: {
      startup: 40000,
      midmarket: 80000,
      enterprise: 160000,
      largeEnterprise: 320000,
    },
    perSiteCost: {
      startup: 10000,
      midmarket: 25000,
      enterprise: 50000,
      largeEnterprise: 100000,
    },
    hostingPerSite: {
      startup: 10000,
      midmarket: 20000,
      enterprise: 40000,
      largeEnterprise: 80000,
    },
    maintenanceHoursPerMonth: {
      startup: 45,
      midmarket: 65,
      enterprise: 95,
      largeEnterprise: 135,
    },
    devHoursPerStaticPage: 3.5,
    devHoursPerDynamicPage: 12,
    avgBuildWeeks: 24,
    seoAuditHours: 58,
    redesignMultiplier: 1.42,
    companyTier: 'largeEnterprise',
  },
  coremedia: {
    name: 'CoreMedia',
    category: 'Enterprise DXP',
    licensing: {
      startup: 50000,
      midmarket: 100000,
      enterprise: 200000,
      largeEnterprise: 400000,
    },
    perSiteCost: {
      startup: 15000,
      midmarket: 30000,
      enterprise: 60000,
      largeEnterprise: 120000,
    },
    hostingPerSite: {
      startup: 12000,
      midmarket: 25000,
      enterprise: 50000,
      largeEnterprise: 100000,
    },
    maintenanceHoursPerMonth: {
      startup: 50,
      midmarket: 70,
      enterprise: 100,
      largeEnterprise: 145,
    },
    devHoursPerStaticPage: 3.5,
    devHoursPerDynamicPage: 13,
    avgBuildWeeks: 26,
    seoAuditHours: 60,
    redesignMultiplier: 1.45,
    companyTier: 'largeEnterprise',
  },
};

// Helper functions to get tiered values
export const getCmsLicensing = (cms, companySize) => {
  if (typeof cms.licensing === 'object') {
    return cms.licensing[companySize] ?? cms.licensing.midmarket ?? 0;
  }
  return cms.baseLicense ?? 0;
};

export const getCmsPerSiteCost = (cms, companySize) => {
  if (typeof cms.perSiteCost === 'object') {
    return cms.perSiteCost[companySize] ?? cms.perSiteCost.midmarket ?? 0;
  }
  return cms.perSiteCost ?? 0;
};

export const getCmsHosting = (cms, companySize) => {
  if (typeof cms.hostingPerSite === 'object') {
    return cms.hostingPerSite[companySize] ?? cms.hostingPerSite.midmarket ?? 0;
  }
  return cms.hostingPerSite ?? 0;
};

export const getCmsMaintenanceHours = (cms, companySize) => {
  if (typeof cms.maintenanceHoursPerMonth === 'object') {
    return cms.maintenanceHoursPerMonth[companySize] ?? cms.maintenanceHoursPerMonth.midmarket ?? 20;
  }
  return cms.maintenanceHoursPerMonth ?? 20;
};

// Webflow pricing - Updated 2024/2025
// Webflow pricing scales with company size for enterprise features
export const WEBFLOW = {
  name: 'Webflow',
  licensing: {
    startup: 468,           // CMS plan $39/mo
    midmarket: 948,         // Business plan $79/mo
    enterprise: 25000,      // Enterprise
    largeEnterprise: 50000, // Enterprise+
  },
  perSiteCost: {
    startup: 0,
    midmarket: 0,
    enterprise: 0,
    largeEnterprise: 0,
  },
  hostingPerSite: {
    startup: 0,             // Included
    midmarket: 0,
    enterprise: 0,
    largeEnterprise: 0,
  },
  maintenanceHoursPerMonth: {
    startup: 5,
    midmarket: 8,
    enterprise: 12,
    largeEnterprise: 18,
  },
  devHoursPerStaticPage: 0.5,
  devHoursPerDynamicPage: 2,
  avgBuildWeeks: 6,
  seoAuditHours: 16,
  redesignMultiplier: 1.1,
};

// Helper to get Webflow licensing by company size
export const getWebflowLicensing = (companySize) => {
  return WEBFLOW.licensing[companySize] ?? WEBFLOW.licensing.midmarket;
};

export const getWebflowMaintenanceHours = (companySize) => {
  if (typeof WEBFLOW.maintenanceHoursPerMonth === 'object') {
    return WEBFLOW.maintenanceHoursPerMonth[companySize] ?? WEBFLOW.maintenanceHoursPerMonth.midmarket;
  }
  return WEBFLOW.maintenanceHoursPerMonth ?? 10;
};
